/** Raw JSON shape emitted by MOXy for a Java Map. */
export type MoxyMapDTO<K, V> = Array<{
  key: K;
  value: V;
}>;

/**
 * Concrete value envelope required when a Java Map value is itself a list of DTOs.
 * Without this extra bean, MOXy serializes the nested DTOs as Java identity strings.
 */
export interface MoxyListValueDTO<T> {
  items?: T[];
}

/** Production's StringMapAdapter adds this extra envelope only for Map<String, String>. */
export interface MoxyStringMapDTO {
  entry?: MoxyMapDTO<string, string>;
}

/** Convert a scalar MOXy map or a legacy keyed object into the domain keyed-object shape. */
export function moxyMapToRecord<V>(
  value: MoxyMapDTO<string, V> | Record<string, V> | null | undefined,
): Record<string, V> {
  if (!value) {
    return {};
  }
  return Array.isArray(value) ? Object.fromEntries(value.map((entry) => [entry.key, entry.value])) : value;
}

/** Convert a MOXy map with explicit list-value envelopes into the domain keyed-object shape. */
export function moxyListMapToRecord<V>(
  value: MoxyMapDTO<string, MoxyListValueDTO<V>> | Record<string, V[]> | null | undefined,
): Record<string, V[]> {
  if (!value) {
    return {};
  }
  return Array.isArray(value) ? Object.fromEntries(value.map((entry) => [entry.key, entry.value.items ?? []])) : value;
}

/** Convert the production StringMapAdapter envelope into the domain keyed-object shape. */
export function moxyStringMapToRecord(value: MoxyStringMapDTO | null | undefined): Record<string, string> {
  return Object.fromEntries((value?.entry ?? []).map((entry) => [entry.key, entry.value]));
}

/** Convert a domain keyed object into the StringMapAdapter request envelope expected by MOXy. */
export function recordToMoxyStringMap(value: Record<string, string>): MoxyStringMapDTO {
  return {
    entry: Object.entries(value).map(([key, entryValue]) => ({ key, value: entryValue })),
  };
}
